import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, DropletIcon, AlertTriangleIcon, TrendingUpIcon, SunIcon, WrenchIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { WATER_CHALLENGE_IMAGES as IMG, waterChallengeCopy as c, waterInsights, waterProblems, type Bi, type InsightKey, type InsightTone } from '../../data/waterChallenge';
import { ChallengeLaptop } from './ChallengeLaptop';
const EASE = [0.23, 1, 0.32, 1] as const;
const TONES: Record<InsightTone, {
  bg: string;
  fg: string;
}> = {
  red: {
    bg: '#FDECEC',
    fg: '#E5484D'
  },
  orange: {
    bg: '#FEF1E6',
    fg: '#EA7A2C'
  },
  indigo: {
    bg: '#EEEDFD',
    fg: '#4F46E5'
  }
};
const INSIGHT_ICONS: Record<InsightKey, typeof BarChart3Icon> = {
  high: BarChart3Icon,
  irrigation: DropletIcon,
  leak: AlertTriangleIcon,
  peak: TrendingUpIcon
};
const PROBLEM_ICONS = {
  over: BarChart3Icon,
  leak: DropletIcon,
  drought: SunIcon,
  reactive: WrenchIcon
} as const;
const PHOTO_MASK = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.45) 20%, #000 42%)',
  maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.45) 20%, #000 42%)'
};
type P = {
  isFR: boolean;
};
function InsightCard({
  isFR,
  k
}: P & {
  k: InsightKey;
}) {
  const item = waterInsights.find(i => i.key === k)!;
  const tone = TONES[item.tone];
  const Icon = INSIGHT_ICONS[k];
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <div className="flex items-center gap-3.5 rounded-2xl bg-white border border-[#EEEEF4] px-4 py-3.5 shadow-[0_20px_40px_-26px_rgba(30,30,80,0.45)]" data-id="element-7478" data-mp="145e1tsurtp59">
      <span className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
      background: tone.bg
    }} aria-hidden="true" data-id="element-7479" data-mp="v6sku24y6i6l">
        <Icon className="w-6 h-6" style={{
        color: tone.fg
      }} strokeWidth={2} data-id="element-7480" data-mp="z8dr34mz7y7t" />
      </span>
      <span className="min-w-0" data-id="element-7481" data-mp="l9e37z7afncp">
        <span className="block font-sans text-[15px] font-semibold text-[#121A3A] leading-snug" data-id="element-7482" data-mp="laffg701ifw0">{pick(item.title)}</span>
        {item.lines.map(l => <span key={l.en} className="block font-sans text-[12.5px] text-[#5B6478] leading-snug" data-id="element-7483" data-mp="2rxdbih13aecl">{pick(l)}</span>)}
      </span>
    </div>;
}
function Dot({
  color,
  className
}: {
  color: string;
  className: string;
}) {
  return <span className={`absolute w-3.5 h-3.5 rounded-full border-[3px] border-white shadow ${className}`} style={{
    background: color
  }} aria-hidden="true" data-id="element-7484" data-mp="2j1xfpugrxp4d" />;
}
export function WaterChallenge() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const message = <>
      <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-7485" data-mp="2wxsq1bx3xrw5">{pick(c.eyebrow)}</p>
      <h2 className="font-serif text-[2.5rem] sm:text-[3.4rem] 2xl:text-[4.3rem] leading-[1.04] tracking-tight mt-7" data-id="element-7486" data-mp="1lg9hd40rcwq7">
        {c.navy.map(l => <span key={l.en} className="block text-[#0E1330]" data-id="element-7487" data-mp="1bdnr429hvvwt">{pick(l)}</span>)}
        <span className="block text-[#4F46E5]" data-id="element-7488" data-mp="1gch13kpkp4s8">{pick(c.indigo)}</span>
      </h2>
      <div className="mt-7 max-w-[560px] font-sans text-[17px] 2xl:text-[19px] leading-[1.6] text-[#4A5470]" data-id="element-7489" data-mp="1l9z1gmpiybom">
        {c.body.map(b => <p key={b.en} data-id="element-7490" data-mp="1q2s9pkk7c25g">{pick(b)}</p>)}
      </div>
      <span className="block w-14 h-[3px] rounded-full bg-[#4F46E5] mt-8" aria-hidden="true" data-id="element-7491" data-mp="1u3c6gn0v53r2" />
    </>;
  return <div className="relative" data-id="element-7492" data-mp="1z0vv8gv6syx9">
      {/* Desktop */}
      <div className="hidden xl:block relative min-h-[780px]" data-id="element-7493" data-mp="2410bq6w3htnh">
        <div className="absolute inset-y-0 right-0 w-[70%]" data-id="element-7494" data-mp="241jh6o4k6pkm">
          <img src={IMG.aerial} alt={pick(c.aerialAlt)} className="w-full h-full object-cover object-[center_40%]" style={PHOTO_MASK} data-id="element-7495" data-mp="290ml258sypr0" />
        </div>

        <div className="relative max-w-[1760px] mx-auto min-h-[780px]" data-id="element-7496" data-mp="9p21c1m1urq0">
          {/* Connectors */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1760 780" preserveAspectRatio="none" aria-hidden="true" data-id="element-7497" data-mp="eo69qq15glfa">
            <path d="M1232 160 L1232 215" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="3 5" data-id="element-7498" data-mp="204vx5yxl2abm" />
            <path d="M1560 245 L1520 245" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="3 5" data-id="element-7499" data-mp="2e43yoyh7xuiz" />
            <path d="M1600 470 L1650 520" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="3 5" data-id="element-7500" data-mp="294iykkqbbg4o" />
          </svg>

          {/* Card 1 with sprinkler house */}
          <motion.div {...reveal} transition={{
          duration: 0.3,
          delay: 0.1,
          ease: EASE
        }} className="absolute z-10 top-[50px] left-[41%] w-[265px]" data-id="element-7501" data-mp="1lcu0enqt7xft">
            <InsightCard isFR={isFR} k="high" data-id="element-7502" data-mp="1v61ecwzm8lri" />
            <div className="relative ml-3 mt-0 w-[150px] h-[104px] rounded-b-xl border-[3px] border-white overflow-hidden shadow-lg" data-id="element-7503" data-mp="1v6ug1jjpwl2s">
              <img src={IMG.sprinkler} alt="" className="w-full h-full object-cover" aria-hidden="true" data-id="element-7504" data-mp="12nx6ikzbr6y4" />
            </div>
            <Dot color="#E5484D" className="left-[158px] top-[182px]" data-id="element-7505" data-mp="xoudy00j6t71" />
          </motion.div>

          <motion.div {...reveal} transition={{
          duration: 0.3,
          delay: 0.15,
          ease: EASE
        }} className="absolute z-10 top-[90px] left-[64%] w-[240px]" data-id="element-7506" data-mp="39c5w8203uws3">
            <InsightCard isFR={isFR} k="irrigation" data-id="element-7507" data-mp="30c9ekkc4uy6j" />
          </motion.div>
          <Dot color="#F28C38" className="left-[calc(70%-7px)] top-[214px]" data-id="element-7508" data-mp="30e3xjt3ugg7z" />

          {/* Card 3 with night house */}
          <motion.div {...reveal} transition={{
          duration: 0.3,
          delay: 0.2,
          ease: EASE
        }} className="absolute z-10 top-[108px] right-[4%] w-[235px]" data-id="element-7509" data-mp="3t0yiss8hjjmi">
            <InsightCard isFR={isFR} k="leak" data-id="element-7510" data-mp="vumz0iblp7v" />
            <div className="ml-auto mr-[-14px] w-[150px] h-[118px] rounded-xl border-[3px] border-white overflow-hidden shadow-lg" data-id="element-7511" data-mp="3j2r46g1o5dno">
              <img src={IMG.night} alt="" className="w-full h-full object-cover" aria-hidden="true" data-id="element-7512" data-mp="3o12cgmcbfg3s" />
            </div>
          </motion.div>
          <Dot color="#E5484D" className="right-[calc(4%+152px)] top-[245px] z-20" data-id="element-7513" data-mp="27makubmzucvj" />

          <motion.div {...reveal} transition={{
          duration: 0.3,
          delay: 0.25,
          ease: EASE
        }} className="absolute z-20 top-[385px] right-[1%] w-[225px]" data-id="element-7514" data-mp="2co9pdougzfu2">
            <InsightCard isFR={isFR} k="peak" data-id="element-7515" data-mp="1oj8rw2mta8yc" />
          </motion.div>
          <Dot color="#4F46E5" className="right-[6%] top-[515px]" data-id="element-7516" data-mp="1ol3avbeivqzs" />

          <motion.div {...reveal} transition={{
          duration: 0.3,
          delay: 0.05,
          ease: EASE
        }} className="absolute z-10 top-[275px] left-[44%] w-[43%] max-w-[760px]" data-id="element-7517" data-mp="1jqz2qotitcx4">
            <ChallengeLaptop isFR={isFR} data-id="element-7518" data-mp="19um0h2jwtqqr" />
          </motion.div>

          <motion.div {...reveal} transition={{
          duration: 0.3,
          ease: EASE
        }} className="relative z-20 w-[43%] pl-6 2xl:pl-12 pt-20 pb-48" data-id="element-7519" data-mp="19rzu368r9ha4">
            {message}
          </motion.div>
        </div>

        <svg className="absolute bottom-0 left-0 w-full h-[170px] pointer-events-none z-20" viewBox="0 0 1440 170" preserveAspectRatio="none" aria-hidden="true" data-id="element-7520" data-mp="10viiy30e5jed">
          <path d="M0 170 L0 40 C 260 20, 460 110, 820 110 C 1100 110, 1300 70, 1440 10 L1440 170 Z" fill="#FFFFFF" data-id="element-7521" data-mp="2v649c3wrvvm0" />
        </svg>
      </div>

      {/* Tablet / mobile */}
      <div className="xl:hidden px-6 max-w-[1100px] mx-auto" data-id="element-7522" data-mp="2xsde7qo2uzm2">
        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-7523" data-mp="2xpr2rfy2fzyk">
          {message}
        </motion.div>
        <div className="relative mt-10 rounded-[24px] overflow-hidden" data-id="element-7524" data-mp="37hogatcjr40g">
          <img src={IMG.aerial} alt={pick(c.aerialAlt)} className="absolute inset-0 w-full h-full object-cover" data-id="element-7525" data-mp="37jriscuvypm2" />
          <div className="relative p-4 sm:p-6 pt-24 sm:pt-32" data-id="element-7526" data-mp="3cikk76wweq5v">
            <ChallengeLaptop isFR={isFR} data-id="element-7527" data-mp="2euk8yhpzedli" />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7528" data-mp="2jraon3xvj76e">
          {waterInsights.map(i => <InsightCard key={i.key} isFR={isFR} k={i.key} data-id="element-7529" data-mp="2oq42ixh2p073" />)}
        </div>
      </div>

      {/* Four problems */}
      <ul className="relative z-30 max-w-[1500px] mx-auto px-6 pt-14 xl:-mt-20 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-y-10" data-id="element-7530" data-mp="2su3gp9evrmxa">
        {waterProblems.map((p, i) => {
        const Icon = PROBLEM_ICONS[p.key];
        return <li key={p.key} className={`flex items-start gap-5 xl:px-8 ${i > 0 ? 'xl:border-l xl:border-[#E6E6F0]' : ''}`} data-id="element-7531" data-mp="u3tr8gs6nf69" data-mpi={i}>
              <span className="w-[76px] h-[76px] rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7532" data-mp="p2w03ep73asm">
                <Icon className="w-8 h-8 text-[#4F46E5]" strokeWidth={1.6} data-id="element-7533" data-mp="z2mvrjphrvua" />
              </span>
              <span className="pt-1" data-id="element-7534" data-mp="1cu1quowobnhp">
                <span className="block font-sans text-[14px] font-bold uppercase tracking-[0.16em] leading-[1.5] text-[#4F46E5] max-w-[220px]" data-id="element-7535" data-mp="1ct02jiqnd4i1">{pick(p.title)}</span>
                <span className="block font-sans text-[15px] leading-snug text-[#5B6478] mt-1.5 max-w-[230px]" data-id="element-7536" data-mp="1mtagj5kiafy9">{pick(p.text)}</span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}